import { DatePipe } from '@angular/common';
import { Component, effect, inject, Injector, Input, OnInit, runInInjectionContext } from '@angular/core';
import { Station } from '@vueling-ng/vy-cdk-searcher';
import {
  FlightJourneyConnectorColorEnum,
  FlightJourneyVariantEnum
} from '@vueling-ng/vy-ds/src/lib/components/globals/flight-journey';
import { RouteTitleConfig } from '@vueling-ng/vy-ds/src/lib/components/globals/route-title';
import { SidePanelService } from '@vueling-ng/vy-ds/src/lib/components/globals/side-panel';
import { LanguageService } from 'app/services/internal/language/language.service';
import { FlightJourney } from '../../models/booking-details.model';
import { FlightSummaryData } from '../../models/flight-summary.model';
import { DsComponentsMapper } from '../../services/ds-components.mapper';
import { FlightDetailComponent } from '../flight-detail/flight-detail.component';

@Component({
  selector: 'app-flight-journeys-summary',
  templateUrl: './flight-journeys-summary.component.html'
})
export class FlightJourneysSummaryComponent implements OnInit {
  @Input({ required: true })
  public flightJourneysData: FlightJourney[] = [];
  @Input({ required: true })
  public paxAmount = 0;
  @Input({ required: true })
  public stations: Station[] = [];

  private readonly _dsComponentsMapper = inject(DsComponentsMapper);
  private readonly _sidePanelService = inject(SidePanelService);
  private readonly _languageService = inject(LanguageService);
  private readonly _datePipe: DatePipe = inject(DatePipe);
  private readonly _injector = inject(Injector);

  public readonly currentLang = this._languageService.currentLanguage;
  public flightSummaryData: FlightSummaryData | undefined;
  private previousLang = '';

  public ngOnInit(): void {
    this.flightSummaryData = this.getFlightSummaryData(this.flightJourneysData);
    this.checkChangeLanguage();
  }

  private loadSummaryData(): void {
    this.flightSummaryData!.routeTitleOutbound.subtitle = this.mapJourneySubtitle(this.flightJourneysData[0]);
    if (this.flightSummaryData!.routeTitleReturn) {
      this.flightSummaryData!.routeTitleReturn.subtitle =
        this.flightJourneysData[1] !== undefined ? this.mapJourneySubtitle(this.flightJourneysData[1]) : undefined;
    }
  }

  private mapJourneySubtitle(journey: FlightJourney): string {
    return (
      this._datePipe.transform(journey.segments[0].departureDate, 'EEEE, dd MMMM', undefined, this.currentLang()) ?? ''
    );
  }

  public openFlightsInfoSidePanel(journeyNumber: number): void {
    const currentJourney = this.flightJourneysData[journeyNumber];
    const origin = this._dsComponentsMapper.findStation(
      currentJourney.segments[currentJourney.segments.length - 1].iataOrigin,
      this.stations
    );
    const destination = this._dsComponentsMapper.findStation(
      currentJourney.segments[currentJourney.segments.length - 1].iataDestination,
      this.stations
    );
    this._sidePanelService.open(FlightDetailComponent, {
      customData: {
        journeyData: this.flightJourneysData[journeyNumber],
        options: {
          variant: FlightJourneyVariantEnum.VERTICAL,
          connectorColor: FlightJourneyConnectorColorEnum.LIGHT
        },
        stations: this.stations
      },
      title: origin + ' - ' + destination
    });
  }

  private getFlightSummaryData(flightsData: FlightJourney[]): FlightSummaryData {
    const outboundJourney = flightsData[0];
    const returnJourney = flightsData[1];

    return {
      flightJourneyDataOutbound: this._dsComponentsMapper.mapFlightJourneyData(outboundJourney, this.stations),
      flightJourneyDataReturn: this._dsComponentsMapper.mapFlightJourneyData(returnJourney, this.stations),
      flightDetailsDataOutbound: this._dsComponentsMapper.mapFlightDetails(outboundJourney),
      flightDetailsDataReturn: this._dsComponentsMapper.mapFlightDetails(returnJourney),
      hasConnectionOutbound: !!outboundJourney.connectionTime,
      hasConnectionReturn: !!returnJourney?.connectionTime,
      routeTitleOutbound: this.mapRouteTitle(outboundJourney)!,
      routeTitleReturn: this.mapRouteTitle(returnJourney) ?? {},
      paxAmount: this.paxAmount,
      title: undefined,
      flightJourneyImageAltOutbound: undefined,
      flightJourneyImageAltReturn: undefined,
      flightJourneyImageOutbound: undefined,
      flightJourneyImageReturn: undefined
    };
  }

  private mapRouteTitle(journey: FlightJourney): RouteTitleConfig | undefined {
    if (!journey) {
      return undefined;
    }

    const firstSegment = journey.segments[0];
    const lastSegment = journey.segments.at(-1);

    return {
      origin: {
        label: this._dsComponentsMapper.findStation(firstSegment.iataOrigin, this.stations)
      },
      destination: {
        label: this._dsComponentsMapper.findStation(lastSegment!.iataDestination, this.stations)
      },
      subtitle: this.mapJourneySubtitle(journey)
    };
  }

  private checkChangeLanguage(): void {
    runInInjectionContext(this._injector, () => {
      effect(() => {
        if (this.currentLang() && this.flightSummaryData && this.previousLang !== this.currentLang()) {
          this.previousLang = this.currentLang();
          this.loadSummaryData();
        }
      });
    });
  }
}
